import React, { useState, useEffect } from "react";
import { IconButton, Badge, Tooltip, CircularProgress } from "@mui/material";
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { SocialService } from "../../services/social-service";
import AuthPrompt from "../Auth/AuthPrompt";

interface LikeButtonProps {
  outfitId: string;
  initialLikeCount: number;
  initialLiked?: boolean;
  size?: "small" | "medium" | "large";
  showCount?: boolean;
  onLikeChange?: (liked: boolean) => void;
}

/**
 * Like button component for outfits
 */
const LikeButton: React.FC<LikeButtonProps> = ({
  outfitId,
  initialLikeCount,
  initialLiked = false,
  size = "medium",
  showCount = true,
  onLikeChange,
}) => {
  const { isAuthenticated, user: authUser } = useAuth();

  // State
  const [isLiked, setIsLiked] = useState<boolean>(initialLiked);
  const [likeCount, setLikeCount] = useState<number>(initialLikeCount);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasCheckedInitialStatus, setHasCheckedInitialStatus] = useState<boolean>(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState<boolean>(false);

  // Check initial like status from user's likedOutfits array (no API call needed)
  useEffect(() => {
    if (isAuthenticated && authUser && !hasCheckedInitialStatus) {
      const liked = (authUser.likedOutfits || []).includes(outfitId);
      setIsLiked(liked);
      setHasCheckedInitialStatus(true);
    } else if (!isAuthenticated) {
      setIsLiked(initialLiked);
      setHasCheckedInitialStatus(true);
    }
  }, [outfitId, isAuthenticated, authUser, initialLiked, hasCheckedInitialStatus]);

  // Handle like button click
  const handleLikeClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!isAuthenticated) {
      // Show authentication prompt dialog
      setShowAuthPrompt(true);
      return;
    }

    if (isLoading) return;

    // Store current state for potential rollback
    const previousLiked = isLiked;
    const previousCount = likeCount;

    try {
      setIsLoading(true);

      // Optimistic update
      const newLikedState = !isLiked;
      setIsLiked(newLikedState);
      setLikeCount(prev => newLikedState ? prev + 1 : Math.max(0, prev - 1));

      // Call the appropriate service method
      if (previousLiked) {
        const result = await SocialService.unlikeOutfit(outfitId);
        if (!result) {
          throw new Error('Unlike operation failed');
        }
      } else {
        const result = await SocialService.likeOutfit(outfitId);
        if (!result) {
          throw new Error('Like operation failed');
        }
      }

      // Notify parent component
      if (onLikeChange) {
        onLikeChange(newLikedState);
      }

    } catch (error) {
      console.error("Error toggling like:", error);
      
      // Revert optimistic update on error (rollback)
      setIsLiked(previousLiked);
      setLikeCount(previousCount);
      
      // Show user-friendly error message
      alert('Failed to update like status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const isDisabled = isLoading;
  
  return (
    <>
      <Tooltip title={!isAuthenticated ? "Sign in to like" : (isLiked ? "Unlike" : "Like")}>
        <span>
          <IconButton
            onClick={handleLikeClick}
            disabled={isDisabled}
            size={size}
            aria-label={isLiked ? "Unlike" : "Like"}
            sx={{
              color: isLiked ? '#e91e63' : 'inherit', // Pink/red color when liked
              '&:hover': {
                color: isLiked ? '#ad1457' : '#e91e63', // Darker pink on hover
              },
              '&.Mui-disabled': {
                color: 'rgba(0, 0, 0, 0.26)',
              }
            }}
          >
            {isLoading ? (
              <CircularProgress size={size === "small" ? 16 : 24} color="inherit" />
            ) : showCount ? (
              <Badge badgeContent={likeCount} color="primary">
                {isLiked ? (
                  <FavoriteIcon sx={{ color: '#e91e63' }} />
                ) : (
                  <FavoriteBorderIcon />
                )}
              </Badge>
            ) : isLiked ? (
              <FavoriteIcon sx={{ color: '#e91e63' }} />
            ) : (
              <FavoriteBorderIcon />
            )}
          </IconButton>
        </span>
      </Tooltip>
      
      {/* Authentication prompt for anonymous users */}
      <AuthPrompt
        open={showAuthPrompt}
        onClose={() => setShowAuthPrompt(false)}
        action="like outfits"
        title="Sign In to Like Outfits"
      />
    </>
  );
};

export default LikeButton;
